<script lang="ts">
	import { locale, _ } from 'svelte-i18n';
	import { docPageSections } from '$lib/docs/content';
	import { availableDocSlugs } from '$lib/docs/loader';
	import type { Language } from '$lib/docs/types';
	import PopularTags from '$lib/components/docs/PopularTags.svelte';

	// Reactive computation for current sections
	// Map short locales to full locale codes
	const getFullLocale = (loc: string): Language => {
		const localeMap: Record<string, Language> = {
			en: 'en',
			sv: 'sv-SE',
			fr: 'fr-FR',
			es: 'es-ES',
			de: 'de-DE',
			ja: 'ja-JP',
			zh: 'zh-CN',
			it: 'it-IT',
			ko: 'ko-KR',
			pt: 'pt-BR',
			vi: 'vi-VN',
			pl: 'pl-PL',
			nl: 'nl-NL',
			nb: 'nb-NO',
			da: 'da-DK',
			fi: 'fi-FI',
			uk: 'uk-UA'
		};
		return (localeMap[loc] || loc || 'en') as Language;
	};

	const currentLocale = $derived(getFullLocale($locale || 'en'));
	// Only show cards for docs that actually exist, so the landing page never links to a 404.
	const sections = $derived(
		(docPageSections[currentLocale] || docPageSections['en'] || [])
			.map((section) => ({
				...section,
				items: section.items.filter((item) => availableDocSlugs.has(item.slug))
			}))
			.filter((section) => section.items.length > 0)
	);
</script>

<svelte:head>
	<title>Documentation - Heyweek</title>
	<meta
		name="description"
		content="The complete Heyweek documentation — step-by-step tutorials, CLI and API references, integrations, and best practices for time tracking and invoicing."
	/>
	<meta property="og:title" content="Documentation - Heyweek" />
	<meta property="og:description" content="The complete Heyweek documentation — step-by-step tutorials, CLI and API references, integrations, and best practices for time tracking and invoicing." />
	<meta name="robots" content="index, follow" />
</svelte:head>

<div class="docs-home">
	<header class="docs-home__header">
		<h1 class="docs-home__title">{$_('docs.overview.title')}</h1>
		<p class="docs-home__description">
			{$_('docs.overview.description')}
		</p>
	</header>

	<!-- Popular Tags Section -->
	<div class="docs-home__tags">
		<PopularTags locale={currentLocale} limit={12} />
	</div>

	<div class="docs-home__sections">
		{#if sections && sections.length > 0}
			{#each sections as section}
				<section class="docs-section">
					<h2 class="docs-section__title">
						{#if section.id === 'popular'}
							{$_('docs.sections.popular')}
						{:else if section.id === 'basics'}
							{$_('docs.sections.basics')}
						{:else}
							{section.title}
						{/if}
					</h2>
					<div class="docs-section__grid">
						{#each section.items as item}
							<a href="/docs/{item.slug}" class="docs-card">
								<div class="docs-card__content">
									<h3 class="docs-card__title">{item.title}</h3>
									<p class="docs-card__description">{item.description}</p>
								</div>
								<svg
									class="docs-card__arrow"
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								>
									<line x1="5" y1="12" x2="19" y2="12"></line>
									<polyline points="12 5 19 12 12 19"></polyline>
								</svg>
							</a>
						{/each}
					</div>
				</section>
			{/each}
		{/if}
	</div>

	<footer class="docs-home__footer">
		<div class="docs-footer__content">
			<div class="docs-footer__section">
				<h3 class="docs-footer__title">{$_('docs.overview.footer.helpTitle')}</h3>
				<p class="docs-footer__text">{$_('docs.overview.footer.helpText')}</p>
			</div>
			<div class="docs-footer__links">
				<a href="/docs/contact-support" class="docs-footer__link">
					<svg
						width="16"
						height="16"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
					>
						<path
							d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
						></path>
					</svg>
					{$_('docs.overview.footer.contactSupport')}
				</a>
				<a href="https://discord.com/invite/pgvtafGhpn" class="docs-footer__link" target="_blank">
					<svg
						width="16"
						height="16"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
					>
						<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
						<circle cx="9" cy="7" r="4"></circle>
						<path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
						<path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
					</svg>
					{$_('docs.overview.footer.joinCommunity')}
				</a>
			</div>
		</div>
	</footer>
</div>

<style>
	.docs-home {
		max-width: 900px;
		margin: 0 auto;
		padding: 2rem 1.5rem;
	}

	.docs-home__header {
		margin-bottom: 2rem;
	}

	.docs-home__title {
		font-size: 1.5rem;
		font-weight: 600;
		color: var(--text-primary);
		margin-bottom: 0.5rem;
		line-height: 1.3;
	}

	.docs-home__description {
		font-size: 0.875rem;
		color: var(--text-secondary);
		line-height: 1.5;
	}

	.docs-home__tags {
		margin-bottom: 2rem;
	}

	.docs-home__sections {
		margin-bottom: 4rem;
	}

	.docs-section {
		margin-bottom: 2rem;
	}

	.docs-section__title {
		font-size: 0.75rem;
		font-weight: 600;
		color: var(--text-tertiary);
		text-transform: uppercase;
		letter-spacing: 0.1em;
		margin-bottom: 0.75rem;
	}

	.docs-section__grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
		gap: 0.75rem;
	}

	.docs-card {
		display: flex;
		flex-direction: column;
		padding: 1rem;
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: 6px;
		text-decoration: none;
		transition: all 0.2s ease;
		position: relative;
		min-height: 90px;
	}

	.docs-card:hover {
		border-color: var(--border-default);
		background: var(--bg-tertiary);
	}

	.docs-card__content {
		flex: 1;
	}

	.docs-card__title {
		font-size: 0.875rem;
		font-weight: 500;
		color: var(--text-primary);
		margin: 0 0 0.375rem 0;
		line-height: 1.4;
	}

	.docs-card__description {
		font-size: 0.75rem;
		color: var(--text-tertiary);
		margin: 0;
		line-height: 1.5;
	}

	.docs-card__arrow {
		position: absolute;
		top: 1.25rem;
		right: 1.25rem;
		color: var(--text-tertiary);
		transition: all 0.2s ease;
		opacity: 0;
	}

	.docs-card:hover .docs-card__arrow {
		opacity: 1;
		transform: translateX(2px);
	}

	.docs-home__footer {
		margin-top: 4rem;
		padding-top: 2rem;
		border-top: 1px solid var(--border-light);
	}

	.docs-footer__content {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 2rem;
	}

	.docs-footer__section {
		flex: 1;
	}

	.docs-footer__title {
		font-size: 0.875rem;
		font-weight: 500;
		color: var(--text-primary);
		margin: 0 0 0.375rem 0;
	}

	.docs-footer__text {
		font-size: 0.75rem;
		color: var(--text-secondary);
		margin: 0;
		line-height: 1.5;
	}

	.docs-footer__links {
		display: flex;
		gap: 1rem;
	}

	.docs-footer__link {
		display: inline-flex;
		align-items: center;
		gap: 0.375rem;
		padding: 0.375rem 0.75rem;
		border-radius: 4px;
		text-decoration: none;
		color: var(--text-secondary);
		border: 1px solid var(--border-light);
		transition: all 0.2s ease;
		font-size: 0.75rem;
		white-space: nowrap;
	}

	.docs-footer__link:hover {
		color: var(--text-primary);
		border-color: var(--border-default);
		background: var(--bg-secondary);
	}

	.docs-footer__link svg {
		width: 16px;
		height: 16px;
	}

	/* Dark mode adjustments */
	:global([data-theme='dark']) .docs-card {
		background: rgba(255, 255, 255, 0.02);
		border-color: var(--border-light);
	}

	:global([data-theme='dark']) .docs-card:hover {
		background: rgba(255, 255, 255, 0.04);
		border-color: var(--border-default);
	}

	@media (max-width: 768px) {
		.docs-home {
			padding: 2rem 1rem;
		}

		.docs-section__grid {
			grid-template-columns: 1fr;
		}

		.docs-footer__content {
			flex-direction: column;
			align-items: flex-start;
			gap: 1.5rem;
		}

		.docs-footer__links {
			flex-direction: column;
			width: 100%;
		}

		.docs-footer__link {
			justify-content: center;
		}
	}
</style>
